import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { PHYSICIAN_REF, ORG_REF, imageRights } from "@/lib/medical-schema";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { ProcedureFaq } from "@/components/site/ProcedureFaq";
import { faqSchema, type Faq } from "@/components/site/InsuranceProse";
import supportGroup from "@/assets/support-group.jpg.asset.json";
import supportGroup480 from "@/assets/support-group-480.webp.asset.json";
import supportGroup800 from "@/assets/support-group-800.webp.asset.json";
import supportGroup1200 from "@/assets/support-group-1200.webp.asset.json";
import { schemaDateFields } from "@/lib/schema-dates";

const SUPPORT_URL = "https://virginiabariatric.com/resources/support";
const SUPPORT_OG_IMAGE = "https://virginiabariatric.com/og/support/og-support.jpg";
const SUPPORT_OG_ALT =
  "Bariatric support group meeting at Virginia Bariatric Surgery in Loudoun County, Virginia";

const SUPPORT_FAQS: Faq[] = [
  {
    q: "What is a bariatric support group?",
    a: "It is a regular meeting for people preparing for or recovering from weight-loss surgery. Sessions usually include brief introductions, a short educational talk, and open discussion, guided by a trained facilitator.",
  },
  {
    q: "Where does the Virginia Bariatric support group meet?",
    a: "In-person meetings are held at our Herndon office, serving patients throughout Loudoun County and Northern Virginia.",
  },
  {
    q: "When are the support group meetings?",
    a: "Meetings resume in November on the first Monday night of each month at 6:00 pm, facilitated by Dr. Matthew Fitzer.",
  },
  {
    q: "Do I have to be a Virginia Bariatric patient to attend?",
    a: "No. The group is open to anyone considering or living with weight-loss surgery, including people who had surgery elsewhere and people still researching their options.",
  },
  {
    q: "Is there a cost to join?",
    a: "No. Attending the support group is free, and there is no registration requirement.",
  },
  {
    q: "Can I attend before my weight-loss surgery?",
    a: "Yes. Many attendees come before surgery to hear firsthand what recovery, eating changes, and long-term follow-up are really like.",
  },
  {
    q: "What topics are covered at the meetings?",
    a: "Common topics include portion sizes, protein and hydration goals, weight-loss plateaus, body image and relationships, weight regain, and body-contouring surgery after major weight loss.",
  },
];


export const Route = createFileRoute("/resources/support")({
  head: () => ({
    meta: [
      { title: "Bariatric Support Group | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Join our bariatric support group in Loudoun County, Virginia — ongoing guidance and community before and after weight-loss surgery.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Bariatric Support Group | Virginia Bariatric" },
        { property: "og:description", content: "Join our bariatric support group in Loudoun County, Virginia — ongoing guidance and community before and after weight-loss surgery." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/resources/support" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Bariatric Support Group | Virginia Bariatric" },
        { name: "twitter:description", content: "Join our bariatric support group in Loudoun County, Virginia — ongoing guidance and community before and after weight-loss surgery." },
        { property: "og:image", content: SUPPORT_OG_IMAGE },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: SUPPORT_OG_ALT },
        { name: "twitter:image", content: SUPPORT_OG_IMAGE },
        { name: "twitter:image:alt", content: SUPPORT_OG_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/resources/support" }],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "@id": "https://virginiabariatric.com/resources/support#webpage",
          url: "https://virginiabariatric.com/resources/support",
          name: "Bariatric Support Group",
          headline: "Bariatric Support Group",
          description:
            "Virginia Bariatric Surgery's bariatric support group in Loudoun County, Virginia — ongoing guidance and community before and after weight-loss surgery.",
          keywords:
            "bariatric support group, weight loss surgery support group, Loudoun County, Northern Virginia",
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          breadcrumb: { "@id": "https://virginiabariatric.com/resources/support#breadcrumb" },
          mainEntityOfPage: "https://virginiabariatric.com/resources/support",
          ...schemaDateFields("/resources/support"),
          reviewedBy: PHYSICIAN_REF,
          author: PHYSICIAN_REF,
          publisher: ORG_REF,
          provider: ORG_REF,
          about: { "@type": "MedicalCondition", name: "Obesity" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Bariatric surgery patients and post-op patients",
          },
          medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
          specialty: "https://schema.org/Surgical",
          primaryImageOfPage: {
            "@type": "ImageObject",
            "@id": "https://virginiabariatric.com/resources/support#primaryimage",
            contentUrl: `https://virginiabariatric.com${supportGroup.url}`,
            url: `https://virginiabariatric.com${supportGroup.url}`,
            width: 1920,
            height: 1281,
            caption:
              "Bariatric support group attendees seated together in a welcoming meeting space",
            ...imageRights("https://virginiabariatric.com/resources/support"),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqSchema(SUPPORT_FAQS, SUPPORT_URL)),
      },
    ] : [],
  }),
  component: SupportPage,
});

function SupportPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-4 lg:right-8 top-2 w-48 lg:w-56" />
        <div className="text-center max-w-4xl mx-auto">
          <EditableText
            as="h1"
            contentKey="support.hero.title"
            defaultValue="Bariatric Support Group"
            className="text-4xl md:text-5xl font-serif"
          />

          <EditableText
            as="p"
            contentKey="support.hero.intro"
            defaultValue="Meet monthly in Loudoun County with fellow patients for ongoing encouragement before and after weight-loss surgery."
            className="mt-4 md:mt-6 text-foreground/80 leading-[1.6] text-base md:text-lg"
          />
        </div>
      </PageSection>

      <PageSection className="pt-2 pb-4 md:pb-6">
        <figure className="mx-auto max-w-[33rem] overflow-hidden rounded-2xl shadow-lg">
          <img
            src={supportGroup800.url}
            srcSet={`${supportGroup480.url} 480w, ${supportGroup800.url} 800w, ${supportGroup1200.url} 1200w`}
            sizes="(min-width: 528px) 528px, calc(100vw - 2rem)"
            alt="Diverse group of attendees seated together in a welcoming meeting space"
            width={1920}
            height={1281}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-auto object-cover"
          />
          <figcaption className="sr-only">
            A welcoming weight-loss surgery support meeting
          </figcaption>
        </figure>
      </PageSection>

      <PageSection className="pt-4 pb-8 text-left">
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-5 text-foreground/80 leading-[1.6]">
          <EditableText
            as="h2"
            contentKey="support.body.title"
            defaultValue="What Is a Bariatric Support Group?"
            className="text-2xl md:text-3xl font-serif scroll-mt-24"
          />

          <EditableText
            as="p"
            contentKey="support.body.p1"
            defaultValue="These groups bring together people who are preparing for or recovering from weight-loss surgery, or who are just bariatric-curious. It creates a safe setting where people can share experiences with others who understand things firsthand or who want to understand them. Most bariatric surgery programs sponsor a group, whether in person or online, with sessions led by a designated facilitator who is usually a nurse, social worker, psychologist, registered dietitian, doctor, or sometimes a bariatric patient. Some of our very best facilitators were our patients who allowed us to send them to facilitator training courses. (Facilitator courses are typically held in cities like San Diego and Las Vegas, which has made it easier for us to talk someone into going.)"
          />
          <h2
            id="what-to-expect"
            className="text-2xl md:text-3xl font-serif scroll-mt-24 pt-2"
          >
            What to Expect at a Weight-Loss Surgery Support Meeting
          </h2>
          <EditableText
            as="p"
            contentKey="support.body.p2"
            defaultValue="The standard format of support group meetings is a round of brief introductions, which is followed by a short educational talk of some kind. The topic could be anything: managing portion sizes, hitting protein and hydration goals, navigating plateaus, adjusting to changes in relationships and body image, etc. We’ve had a few plastic surgeons come and give talks about aesthetic surgery after weight loss. Dr. Joseph Michaels (friend-emeritus of the practice) has done a few fantastic ones. The talk is traditionally followed by an open discussion. Questions are asked (and sometimes answered), tips are exchanged, complaints are made (sometimes), etc."
          />
          <h2
            id="before-and-after-surgery"
            className="text-2xl md:text-3xl font-serif scroll-mt-24 pt-2"
          >
            Bariatric Support Before and After Weight-Loss Surgery
          </h2>
          <EditableText
            as="p"
            contentKey="support.body.p3"
            defaultValue="The benefits are practical and emotional. Because weight-loss surgery requires permanent changes to eating, activity, and mindset, hearing how others handle real-world challenges—dining out, cravings, loose skin, weight regain, or unsupportive friends and family—can make the changes feel manageable and less isolating. Regular attendance appears to be associated with better follow-through on lifestyle habits and better results over the long term. Groups normalize the emotional ups and downs that often accompany rapid weight loss (they are normal). A group sometimes can serve as an early-warning system, since fellow members or facilitators may notice signs of complications, disordered eating, or depression and encourage someone to reach out to their care team. Support groups complement and support the practice’s work of ensuring that our patients thrive."
          />
          <h2
            id="loudoun-county-meetings"
            className="text-2xl md:text-3xl font-serif scroll-mt-24 pt-2"
          >
            Our Group in Loudoun County &amp; Herndon
          </h2>
          <EditableText
            as="p"
            contentKey="support.body.p4"
            defaultValue="We have had the privilege of hosting a regular group in Loudoun County for the last fourteen years. The group is currently undergoing a reshuffle due to the retirement of our facilitator for the past ten years, the esteemed and beloved Ms. Deby Wine. (It has become difficult for her to simultaneously manage that responsibility and those of her senior leadership position at a national quilting organization.) The current plan is for the regular support group to resume in November, at which time we will begin having in-person support group meetings at the Herndon office. They will occur on the first Monday night of each month at 6:00 pm, and Dr. Fitzer will be facilitating. Hope to see you there!"
          />
          <h2
            id="who-can-attend"
            className="text-2xl md:text-3xl font-serif scroll-mt-24 pt-2"
          >
            Who Can Attend?
          </h2>
          <p className="text-foreground/80 leading-[1.6]">
            Everyone is welcome: patients who have already had{" "}
            <Link to="/services/sleeve" className="text-brand-mid underline">
              gastric sleeve
            </Link>
            ,{" "}
            <Link to="/services/bypass" className="text-brand-mid underline">
              gastric bypass
            </Link>
            ,{" "}
            <Link to="/services/revisions" className="text-brand-mid underline">
              revision
            </Link>
            , or{" "}
            <Link to="/services/balloon" className="text-brand-mid underline">
              balloon
            </Link>{" "}
            procedures; patients preparing for surgery; people using{" "}
            <Link to="/medical-weight-loss/medications" className="text-brand-mid underline">
              medical weight-loss medications
            </Link>
            ; and
            anyone still researching their options. You do not need to be a
            Virginia Bariatric patient to attend, and there is no cost to join.
          </p>
          <p className="text-foreground/80 leading-[1.6]">
            If you are still deciding whether surgery is right for you, our{" "}
            <Link to="/resources/qualifications" className="text-brand-mid underline">
              qualifying for bariatric surgery
            </Link>{" "}
            guide walks through the BMI and health criteria you need to meet.
          </p>

          <ProcedureFaq
            heading="Support group: frequently asked questions"
            faqs={SUPPORT_FAQS}
          />

          <aside
            aria-labelledby="support-cta"
            className="mt-10 rounded-2xl border border-border bg-muted/40 p-6 md:p-8 text-center"
          >
            <h2 id="support-cta" className="font-serif text-2xl md:text-3xl scroll-mt-24">
              Save your spot at the next support group meeting
            </h2>
            <p className="mt-3 text-foreground/80 leading-[1.6]">
              Meetings are free and open to everyone. Send us a message and we
              will email you the date, time, and directions to the Herndon
              office — or call us at{" "}
              <a
                href="tel:7037099771"
                aria-label="Call Virginia Bariatric Surgery at 703-709-9771"
                className="text-brand-logo font-medium"
              >
                (703) 709-9771
              </a>
              .
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                search={{ topic: "support" }}
                className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Sign up for the support group
              </Link>
              <Link
                to="/appointments"
                className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
              >
                Request an appointment
              </Link>
            </div>
          </aside>

        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Contact Us
          </Link>
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Resources
          </Link>
        </div>

      <LastUpdated className="mt-10" />
      </PageSection>
    </>
  );
}
